import { useCallback, useSyncExternalStore } from 'react';

/**
 * SF-112: holds the admin session in memory only — a module-level variable, not
 * `localStorage`/`sessionStorage` — via this small dedicated store, kept entirely separate from
 * the player `ApiClient` singleton (`api/client.ts`). The two must never share a slot, or a page
 * holding both a player and an admin session would risk one clobbering the other's
 * `Authorization` header. In-memory-only means a page refresh logs the admin out and requires
 * re-login — an accepted tradeoff for this low-traffic surface (smaller XSS blast radius than
 * `sessionStorage`).
 *
 * Uses `useSyncExternalStore` rather than a plain per-instance `useState` mirror: `adminClient.ts`
 * clears the session from outside any component (on a 401 from any `/api/admin/**` call), and
 * every currently-mounted `useAdminAuth()` consumer (e.g. `ChancelleryShell`'s chip,
 * `RequireAdminAuth`'s guard) must react to that immediately, not just a component that happens to
 * mount fresh afterward.
 */
interface AdminSession {
  token: string;
  username: string;
}

let session: AdminSession | null = null;
const listeners = new Set<() => void>();

function notify() {
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot(): AdminSession | null {
  return session;
}

/** Test-only reset helper, mirrors `useDiplomacy`'s `_resetDiplomacyCache()`. */
export function _resetAdminAuth() {
  session = null;
  notify();
}

/**
 * Returns the current admin token for `adminClient.ts` to attach as a Bearer header. Not itself
 * reactive (it's a plain function, not a hook) — components should use `useAdminAuth()` instead.
 */
export function getAdminToken(): string | null {
  return session?.token ?? null;
}

/** Sets the in-memory session and notifies every mounted `useAdminAuth()` consumer. */
export function setAdminSession(token: string, username: string) {
  session = { token, username };
  notify();
}

/**
 * Clears the in-memory session and notifies every mounted `useAdminAuth()` consumer — called by
 * `adminClient.ts` on a 401 from any `/api/admin/**` call, and by `ChancelleryShell`'s logout button.
 */
export function clearAdminSession() {
  session = null;
  notify();
}

interface UseAdminAuthResult {
  token: string | null;
  username: string | null;
  isAuthenticated: boolean;
  setSession: (token: string, username: string) => void;
  logout: () => void;
}

export function useAdminAuth(): UseAdminAuthResult {
  const current = useSyncExternalStore(subscribe, getSnapshot);

  const setSession = useCallback((token: string, username: string) => {
    setAdminSession(token, username);
  }, []);

  const logout = useCallback(() => {
    clearAdminSession();
  }, []);

  return {
    token: current?.token ?? null,
    username: current?.username ?? null,
    isAuthenticated: current !== null,
    setSession,
    logout,
  };
}
